import type { Nullable } from '../../../types/nullable'

class DateHelpers {
  /**
   * Tries to parse date strings into Date objects.
   * Will return null if input is null or ''
   * @param {string} potentialDate A date to be transformed into a Date
   * @returns
   */
  static transformDeliveryDate (potentialDate: Nullable<string>): Nullable<Date> {
    if (potentialDate === null || potentialDate === '') {
      return null
    }

    // Basket dates are calendar dates, not timezone-aware timestamps.
    // Props are expected as "YYYY-MM-DD", with the selected date sometimes
    // coming through as "YYYY-MM-DDT00:00:00...". In both cases the leading
    // date is the business value; do not parse it as a timestamp.
    const [year, month, day] = potentialDate.slice(0, 10).split('-').map(Number)
    if (year && month && day) {
      return new Date(
        year,
        month - 1,
        day
      )
    }

    // Preserve support for older display-style date strings such as "21 Apr, 2021".
    // TODO: Check if this legacy format is still needed. Current basket
    // payloads do not use it, but existing tests cover it. Keep it as a local
    // calendar date, not a UTC timestamp.
    const date = new Date(potentialDate)
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    )
  }

  static transformDeliveryDates (deliveryDates: string[]): Date[] {
    return deliveryDates.map((givenDeliveryDate: string): Date => (
      this.transformDeliveryDate(givenDeliveryDate)
    ))
  }

  static formatDeliveryDate (deliveryDate: Nullable<Date>): string {
    if (deliveryDate === null) {
      return ''
    }

    const month = String(deliveryDate.getMonth() + 1).padStart(2, '0')
    const day = String(deliveryDate.getDate()).padStart(2, '0')

    return `${deliveryDate.getFullYear()}-${month}-${day}`
  }

  /**
   * Checks the unix timestamp of two dates to see whether they are
   * equal or not.
   * @param dateA First date
   * @param dateB Second date
   * @returns {boolean} - whether they are equal
   */
  static isEqual (dateA: Date, dateB: Date): boolean {
    return dateA.getTime() === dateB.getTime()
  }

  /**
   * Will check if a date is inside an array by comparing unix timestamp of both
   * dates.
   * @param needle The date you're checking
   * @param haystack The array you want to check in
   * @returns {Date}
   */
  static isInArray (needle: Date, haystack: Date[]): boolean {
    return haystack.some((date: Date): boolean => (
      this.isEqual(needle, date)
    ))
  }
}

export default DateHelpers
